import React, { useEffect, useRef } from 'react';

const compactChipStyle = {
  height: '22px',
  minWidth: '22px',
  padding: '2px 7px',
  borderRadius: '6px',
  border: '1px solid #d0d4da',
  background: '#fff',
  color: '#4b5563',
  fontSize: '12px',
  lineHeight: '1',
  fontWeight: 500,
  cursor: 'pointer',
};

const compactChipActiveStyle = {
  borderColor: '#3366ff',
  background: '#3366ff',
  color: '#fff',
};

const isIndexSelected = (index, selectedRange) => {
  if (!selectedRange) {
    return false;
  }

  const start = Math.min(selectedRange.start, selectedRange.end);
  const end = Math.max(selectedRange.start, selectedRange.end);

  return index >= start && index <= end;
};

const RangeChips = ({
  groupKey,
  label,
  options,
  selectedRange,
  onChange,
}) => {
  const dragAnchorRef = useRef(null);
  const isDraggingRef = useRef(false);
  const didDragRef = useRef(false);

  useEffect(() => {
    const stopDragging = () => {
      isDraggingRef.current = false;
      dragAnchorRef.current = null;
    };

    window.addEventListener('mouseup', stopDragging);

    return () => {
      window.removeEventListener('mouseup', stopDragging);
    };
  }, []);

  const selectRangeByDrag = (currentIndex) => {
    if (!isDraggingRef.current || dragAnchorRef.current === null) {
      return;
    }

    if (dragAnchorRef.current === currentIndex) {
      return;
    }

    didDragRef.current = true;

    onChange({
      start: Math.min(dragAnchorRef.current, currentIndex),
      end: Math.max(dragAnchorRef.current, currentIndex),
    });
  };

  const startSelection = (index) => {
    isDraggingRef.current = true;
    dragAnchorRef.current = index;
  };


  const applyClickSelection = (index) => {
    if (didDragRef.current) {
      didDragRef.current = false;
      return;
    }

    if (!selectedRange) {
      onChange({ start: index, end: index });
      return;
    }

    const start = Math.min(selectedRange.start, selectedRange.end);
    const end = Math.max(selectedRange.start, selectedRange.end);

    if (start === end) {
      if (start === index) {
        onChange(null);
        return;
      }

      onChange({
        start: Math.min(start, index),
        end: Math.max(start, index),
      });
      return;
    }

    onChange({ start: index, end: index });
  };

  return (
    <fieldset>
      <label>{label}</label>
      <div className="d-flex flex-wrap" style={{ gap: '4px' }}>
        {options.map((option) => {
          const active = isIndexSelected(option.index, selectedRange);

          return (
            <button
              key={`${groupKey}-${option.testId}`}
              type="button"
              data-testid={`${groupKey}-chip-${option.testId}`}
              data-active={active ? 'true' : 'false'}
              onMouseDown={() => startSelection(option.index)}
              onMouseEnter={() => selectRangeByDrag(option.index)}
              onClick={() => applyClickSelection(option.index)}
              style={active ? { ...compactChipStyle, ...compactChipActiveStyle } : compactChipStyle}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
};

export const MapFilters = ({
  confirmationsFilter,
  onConfirmationsChange,
  warehouseDaysFilter,
  onWarehouseDaysChange,
}) => {
  const confirmationOptions = [
    { label: '0', testId: '0', index: 0 },
    { label: '1', testId: '1', index: 1 },
    { label: '2', testId: '2', index: 2 },
    { label: '3', testId: '3', index: 3 },
    { label: '4', testId: '4', index: 4 },
    { label: '5+', testId: '5plus', index: 5 },
  ];

  const warehouseOptions = [
    { label: '0', testId: '0', index: 0 },
    { label: '1', testId: '1', index: 1 },
    { label: '2', testId: '2', index: 2 },
    { label: '3', testId: '3', index: 3 },
    { label: '4', testId: '4', index: 4 },
    { label: '5', testId: '5', index: 5 },
    { label: '6', testId: '6', index: 6 },
    { label: '7', testId: '7', index: 7 },
    { label: '8', testId: '8', index: 8 },
    { label: '9', testId: '9', index: 9 },
    { label: '10+', testId: '10plus', index: 10 },
  ];

  return (
    <div className="mt-1 mb-2">
      <div
        className="d-flex flex-wrap align-items-start"
        style={{ columnGap: '20px', rowGap: '8px' }}
      >
        <div>
          <RangeChips
            groupKey="confirmations"
            label="Wysłane terminy"
            options={confirmationOptions}
            selectedRange={confirmationsFilter}
            onChange={onConfirmationsChange}
          />
        </div>
        <div>
          <RangeChips
            groupKey="warehouse-days"
            label="Dni na magazynie"
            options={warehouseOptions}
            selectedRange={warehouseDaysFilter}
            onChange={onWarehouseDaysChange}
          />
        </div>
      </div>
    </div>
  );
};
